import {
  announcementGifField,
  announcementImageField,
  type Announcement,
  type AnnouncementPayload,
  type AnnouncementTargetInput,
} from '../../../types/api/announcements.types';
import {
  hasFreshMedia,
  memberKey,
  memberLabel,
  type AudienceSelection,
} from './announcementAudience';
import { ANNOUNCEMENT_STEPS, AnnouncementStep } from './announcementSteps';

/**
 * The intervals the schedule step offers by name, most frequent first. The
 * backend takes any number of minutes; these exist because nobody thinks in
 * minutes, and anything not on the list goes through "Custom" instead.
 */
export const REPEAT_PRESETS = [
  { minutes: 60, label: 'Every hour' },
  { minutes: 360, label: 'Every 6 hours' },
  { minutes: 720, label: 'Every 12 hours' },
  { minutes: 1440, label: 'Once a day' },
  { minutes: 4320, label: 'Every 3 days' },
  { minutes: 10080, label: 'Once a week' },
] as const;

/** The preset value that swaps the select for the raw minutes input. */
export const CUSTOM_REPEAT = 'custom';

/** "Once a day" when the minutes match a preset, "90 min" when they don't. */
export const repeatLabel = (minutes: number): string =>
  REPEAT_PRESETS.find((preset) => preset.minutes === minutes)?.label ??
  `${minutes} min`;

/** Everything the wizard holds that is not the audience. */
export type AnnouncementDraft = {
  title: string;
  body: string;
  active: boolean;
  /** Off means show it once and never repeat; on reveals the interval. */
  repeats: boolean;
  /** A preset's minutes as a string, or CUSTOM_REPEAT for the free input. */
  repeatPreset: string;
  /** Minutes, exactly as typed. Only read while the preset is custom. */
  repeatInterval: string;
  /** Off publishes immediately and forever; on reveals the range picker. */
  showInRange: boolean;
  /** [from, to] from the range picker. Either end may still be missing. */
  dateRange: Date[];
};

export type UpdateAnnouncementDraft = <Key extends keyof AnnouncementDraft>(
  key: Key,
  value: AnnouncementDraft[Key]
) => void;

export const EMPTY_DRAFT: AnnouncementDraft = {
  title: '',
  body: '',
  active: true,
  repeats: false,
  // Once a day, so switching Repeat on lands on a sane answer instead of an
  // unmade choice - a preset can't be left empty the way the old input could.
  repeatPreset: '1440',
  repeatInterval: '',
  showInRange: false,
  dateRange: [],
};

/** The minutes to send, or null when off, empty or zero - all "show once". */
export const draftRepeatMinutes = (draft: AnnouncementDraft): number | null => {
  if (!draft.repeats) return null;

  const minutes =
    draft.repeatPreset === CUSTOM_REPEAT
      ? Number(draft.repeatInterval.trim())
      : Number(draft.repeatPreset);

  return minutes > 0 ? minutes : null;
};

export const draftFromAnnouncement = (
  announcement: Announcement
): AnnouncementDraft => {
  const dateRange: Date[] = [];
  if (announcement.publishedAt)
    dateRange.push(new Date(announcement.publishedAt));
  if (announcement.expiresAt) dateRange.push(new Date(announcement.expiresAt));

  // A saved interval that matches a preset selects it by name; any other
  // number opens as custom with the minutes back in the input.
  const savedRepeat = announcement.repeatInterval;
  const preset = REPEAT_PRESETS.find(({ minutes }) => minutes === savedRepeat);

  return {
    title: announcement.title,
    body: announcement.body,
    active: announcement.active,
    repeats: savedRepeat != null,
    repeatPreset: preset
      ? String(preset.minutes)
      : savedRepeat != null
      ? CUSTOM_REPEAT
      : EMPTY_DRAFT.repeatPreset,
    repeatInterval: savedRepeat != null && !preset ? String(savedRepeat) : '',
    // Either end of the window is enough to mean "this one is scheduled".
    showInRange: !!(announcement.publishedAt || announcement.expiresAt),
    dateRange,
  };
};

/**
 * Why this step cannot be left yet, or null when it is complete.
 *
 * The wizard checks a step on the way out and again on submit, so the rule
 * lives here once. A step with nothing required returns null.
 */
export const stepError = (
  step: number,
  draft: AnnouncementDraft,
  selection: AudienceSelection,
  /** Selected members with no image or GIF, from `membersMissingMedia`. */
  missingMedia: AudienceSelection = []
): string | null => {
  if (step === AnnouncementStep.content) {
    if (!draft.title.trim()) return 'Please enter a title';
    if (!draft.body.trim()) return 'Please enter the body';
  }

  if (step === AnnouncementStep.audience) {
    if (!selection.length) return 'Enable at least one audience member';

    // The API rejects the whole request over one target without an image, so
    // it is caught here where the fix is a click away rather than after a
    // save that names a pair the form never flagged.
    if (missingMedia.length)
      return `An image or a GIF is required for every selected target. Missing: ${listMembers(
        missingMedia
      )}`;
  }

  return null;
};

/** Up to three pairs by name, the rest as a count. */
export const listMembers = (members: AudienceSelection) => {
  const names = members.map(memberLabel);
  if (names.length <= 3) return names.join(', ');

  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
};

/** The first step that still needs something, so submit can jump back to it. */
export const firstInvalidStep = (
  draft: AnnouncementDraft,
  selection: AudienceSelection,
  missingMedia: AudienceSelection = []
): { step: number; message: string } | null => {
  for (let step = 0; step < ANNOUNCEMENT_STEPS.length; step++) {
    const message = stepError(step, draft, selection, missingMedia);
    if (message) return { step, message };
  }

  return null;
};

/**
 * The multipart body the create and update endpoints take: one `announcement`
 * JSON part, plus one file part per target whose group picked a new image
 * or GIF - `image_<app>_<platform>` or `gif_<app>_<platform>`, never both.
 *
 * A target whose media came back from the server is a string URL, not a file,
 * and sending nothing for it is what keeps the stored image or GIF in place.
 */
export const buildAnnouncementFormData = (
  draft: AnnouncementDraft,
  selection: AudienceSelection,
  /** Per member key, the group's saved file copied for a new target. */
  copies: Map<string, File> = new Map()
): FormData => {
  const targets: AnnouncementTargetInput[] = selection.map(
    ({ app, platform, group }) => ({
      app,
      platform,
      minAppVersion: group.minAppVersion.trim() || null,
    })
  );

  const payload: AnnouncementPayload = {
    type: 'whatsNew',
    title: draft.title.trim(),
    body: draft.body,
    active: draft.active,
    repeatInterval: draftRepeatMinutes(draft),
    publishedAt:
      draft.showInRange && draft.dateRange[0]
        ? draft.dateRange[0].toISOString()
        : null,
    expiresAt:
      draft.showInRange && draft.dateRange[1]
        ? draft.dateRange[1].toISOString()
        : null,
    targets,
  };

  const body = new FormData();
  body.append('announcement', JSON.stringify(payload));

  // Exactly one file per target, of the kind the group picked: the backend
  // takes either and answers both at once with a 400. Whichever arrives
  // replaces what is stored and deletes the other kind.
  selection.forEach((member) => {
    const { app, platform, group } = member;
    const file = hasFreshMedia(group)
      ? (group.media as File)
      : copies.get(memberKey(member));
    if (!file) return;

    const field =
      group.mediaKind === 'gif'
        ? announcementGifField(app, platform)
        : announcementImageField(app, platform);
    body.append(field, file);
  });

  return body;
};
